// Anket sonuç raporu — web bölümleri ve PDF modelinin ortak SAF yardımcıları (spec 2026-10-15 §10).
// Rakamlar daima özet kaydından (snapshot) gelir; anlatı yalnız metin/kod/hedef taşır.

export const REPORT_POLL_MS = 5000;
// 48 × 5 sn = 4 dk: kapalı özet kapanıştan ClosedAt + 2 dk sonra hesaplanır (yolda olan gönderimler commit olsun) +
// işçi turu (15 sn) — kapatır kapatmaz açılan sekme zaman aşımına düşmesin.
export const REPORT_MAX_POLLS = 48;
export const AI_JOB_POLL_MS = 5000;
export const SECTION_IDS = ['s01', 's02', 's03', 's04', 's05', 's06', 's07', 's08'];
// Tamamen AI bölümleri — anlatı görünmüyorsa (Read kullanıcısı, yayında değil) bütünüyle gizlenir.
export const NARRATIVE_SECTION_IDS = ['s06', 's07'];

export const sectionNumber = (id) => String(id || '').replace(/^s/, '');

export function resolveLabel(t, label) {
  if (!label) return '';
  if (label.key) {
    const params = {};
    if (label.from !== null && label.from !== undefined) params.from = label.from;
    if (label.to !== null && label.to !== undefined) params.to = label.to;
    return Object.keys(params).length ? t(label.key, params) : t(label.key);
  }
  return label.text || '';
}

export function formatPct(t, value) {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return t('survey.report.pct', { value: Math.round(value) });
}

export function formatMean(value, lang) {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(lang || 'tr', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
}

export function distribution(result) {
  const zero = { unfavorable: 0, neutral: 0, favorable: 0 };
  if (!result) return zero;
  const u = Number(result.unfavorablePct) || 0;
  const n = Number(result.neutralPct) || 0;
  const f = Number(result.favorablePct) || 0;
  const sum = u + n + f;
  if (sum <= 0) return zero;
  return { unfavorable: (u * 100) / sum, neutral: (n * 100) / sum, favorable: (f * 100) / sum };
}

export function questionById(data, id) {
  return (data?.questions || []).find((q) => q.id === id) || null;
}

export function questionsByTopDimension(data) {
  const questions = data?.questions || [];
  const groups = (data?.dimensions || [])
    .map((dimension) => ({ dimension, questions: questions.filter((q) => q.topDimensionId === dimension.id) }))
    .filter((g) => g.questions.length > 0);
  const known = new Set((data?.dimensions || []).map((d) => d.id));
  const orphans = questions.filter((q) => !q.topDimensionId || !known.has(q.topDimensionId));
  if (orphans.length > 0) groups.push({ dimension: null, questions: orphans });
  return groups;
}

export function heatmapColumns(heatmap) {
  return (heatmap?.breakdowns || []).flatMap((b) =>
    (b.groups || []).map((group) => ({ breakdownKey: b.key, label: b.label, group })));
}

export function heatmapPages(heatmap, perPage) {
  const columns = heatmapColumns(heatmap);
  const size = Math.max(1, perPage || 1);
  const pages = [];
  for (let i = 0; i < columns.length; i += size) pages.push(columns.slice(i, i + size));
  return pages;
}

export function narrativeVisible(report) {
  return !!(report && report.narrative);
}

export function themeShare(item, evaluated) {
  if (!evaluated || evaluated <= 0) return 0;
  return ((item?.count || 0) / evaluated) * 100;
}

export function parseThemeRef(ref) {
  if (typeof ref !== 'string') return null;
  const slash = ref.indexOf('/');
  if (slash <= 0 || slash === ref.length - 1) return null;
  return { questionId: ref.slice(0, slash), itemId: ref.slice(slash + 1) };
}

const currentFavorable = (data, code) => {
  const q = (data?.questions || []).find((x) => x.code === code);
  if (!q || q.suppressed) return null;
  return q.favorablePct ?? null;
};

export function indicatorRows(narrative, data) {
  if (!narrative) return [];
  return (narrative.indicators || []).map((ind) => ({
    code: ind.code,
    label: ind.label,
    current: currentFavorable(data, ind.code),
    target6: ind.target6 ?? null,
    target12: ind.target12 ?? null,
  }));
}

export function commentScoreRows(narrative, data) {
  if (!narrative) return [];
  return (narrative.commentScoreLinks || []).map((link) => {
    const ref = parseThemeRef(link.themeRef);
    const block = ref && (narrative.themes || []).find((b) => b.questionId === ref.questionId);
    const item = block && (block.items || []).find((i) => i.id === ref.itemId);
    const codes = link.questionCodes || [];
    return {
      themeName: item ? item.name : '',
      codes,
      favorablePct: codes.length ? currentFavorable(data, codes[0]) : null,
      assessment: link.assessment,
    };
  });
}

const rangeLabel = (t, labelKey, from, to) => {
  if (from > to) return null;
  const label = t(labelKey);
  return from === to
    ? t('survey.report.legendSingle', { label, value: from })
    : t('survey.report.legendRange', { label, from, to });
};

export function scaleLegend(t, method) {
  const min = method?.scaleMin ?? 1;
  const max = method?.scaleMax ?? 5;
  const fav = method?.favorableMin ?? 4;
  const unf = method?.unfavorableMax ?? 2;
  return {
    unfavorable: rangeLabel(t, 'survey.report.unfavorable', min, unf),
    neutral: rangeLabel(t, 'survey.report.neutral', unf + 1, fav - 1),
    favorable: rangeLabel(t, 'survey.report.favorable', fav, max),
  };
}

export function summaryHelpParams(t, method) {
  const strong = method?.summaryBands?.strongMin ?? 70;
  const watch = method?.summaryBands?.watchMin ?? 50;
  return {
    favorableMin: method?.favorableMin ?? 4,
    scaleMin: method?.scaleMin ?? 1,
    scaleMax: method?.scaleMax ?? 5,
    strong: formatPct(t, strong),
    watch: formatPct(t, watch),
    strongBelow: formatPct(t, strong - 1),
  };
}

export function periodText(data, lang) {
  const { publishedAt, closedAt } = data?.survey || {};
  if (!publishedAt && !closedAt) return '—';
  const fmt = new Intl.DateTimeFormat(lang || 'tr', { day: 'numeric', month: 'long', year: 'numeric' });
  const p = publishedAt ? fmt.format(new Date(publishedAt)) : '';
  const c = closedAt ? fmt.format(new Date(closedAt)) : '';
  if (p && c) return `${p} – ${c}`;
  return p ? `${p} –` : `– ${c}`;
}
